<template>
  <v-dialog
    :value="isOpen"
    scrollable
    persistent
    :max-width="maxWidth"
    content-class="gmb-attributes-tree"
  >
    <v-card>
      <v-card-title>
        <div class="w-100 text-center">
          {{ $t(title) }}

          <div v-if="message" class="dialog-message">
            {{ $t(message) }}
          </div>

          <v-text-field
            :value="search"
            prepend-inner-icon="icon-search"
            :label="$t('message.facilityDetails.attributes.findAttributes')"
            single-line
            clearable
            class="search-input"
            :hide-details="true"
            @input="onInput"
          ></v-text-field>
        </div>
      </v-card-title>
      <v-card-text>
        <template v-if="filteredAttributes.count && !loading">
          <GmbAttributesTreeNode
            v-for="groupName in Object.keys(filteredAttributes.items)"
            :key="groupName"
            :group-name="groupName"
            :attributes="filteredAttributes.items[groupName]"
            :delete-mode="deleteMode"
          ></GmbAttributesTreeNode>
        </template>
        <no-data v-else></no-data>
        <SectionLoader :status="searching" />
      </v-card-text>
      <v-card-actions class="justify-center">
        <ui-button class="mr-2" color="primary" outlined @click="close">
          {{ $t("message.common.cancel") }}
        </ui-button>
        <ui-button class="ml-2" color="primary" @click="save">
          {{ $t("message.common.apply") }}
        </ui-button>
      </v-card-actions>
      <SectionLoader :status="loading" :opacity="1" />
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import GmbAttributesTreeNode from "@/components/FacilityDetails/GmbAttributes/GmbAttributesTreeNode";
import SectionLoader from "@/components/Loader/SectionLoader";
import NoData from "@/components/ui/NoData";

import { GmbAttributeValueType } from "@/enums/gmb-attribute-value-type";
import { debounce } from "@/helpers/helpers";

export default {
  name: "GmbAttributesTree",
  components: {
    GmbAttributesTreeNode,
    SectionLoader,
    NoData
  },
  props: {
    facilitiesIds: {
      type: Array,
      required: true
    },
    isOpen: {
      type: Boolean,
      required: true
    },
    facilityAttributes: {
      type: Array,
      required: true
    },
    title: {
      type: String,
      default: () => "message.facilityDetails.attributes.title"
    },
    message: {
      type: String,
      default: () => ""
    },
    deleteMode: {
      type: Boolean,
      default: () => false
    }
  },
  data() {
    return {
      loading: false,
      maxWidth: 533,
      search: "",
      searching: false,
      count: 0
    };
  },
  computed: {
    ...mapState({
      attributes: "attributes/attributes"
    }),
    ...mapGetters({
      groupedAttributes: "attributes/groupedAttributes",
      updatedAttributes: "attributes/updatedAttributes"
    }),
    filteredAttributes() {
      const query = this.search || "";
      return this.groupedAttributes(query);
    }
  },
  async created() {
    await this.fetchAttributes();
  },
  methods: {
    ...mapActions({
      fetchAvailableAttributes: "attributes/fetchAvailableAttributes"
    }),
    async fetchAttributes() {
      this.loading = true;
      await this.fetchAvailableAttributes({
        facilitiesIds: this.facilitiesIds,
        activeAttributes: this.facilityAttributes,
        onlySet: this.deleteMode,
        valueTypes: [
          GmbAttributeValueType.BOOL,
          GmbAttributeValueType.ENUM,
          GmbAttributeValueType.REPEATED_ENUM
        ]
      });
      this.loading = false;
    },
    close() {
      this.$emit("close");
    },
    save() {
      this.$emit("save", this.updatedAttributes);
      this.close();
    },
    onInput(value) {
      this.searching = true;
      this.setSearch(value);
    },
    setSearch: debounce(function(value) {
      this.search = value;
      this.searching = false;
    }, 500)
  }
};
</script>

<style lang="scss">
$paddingX: 68px;
$shadow: 0 0 12px rgba(0, 0, 0, 0.05);

.gmb-attributes-tree {
  .v-card {
    .v-card__title {
      @include font-size(18px);
      font-weight: 600;
      line-height: 22px;
      justify-content: center;
    }

    .v-card__title,
    .v-card__actions {
      padding: 20px $paddingX;
      box-shadow: $shadow;
      z-index: 1;

      .v-btn {
        flex-grow: 1;
      }
    }

    .v-card__text {
      padding-left: $paddingX;
      padding-right: $paddingX;
      position: relative;
    }
  }

  .search-input {
    width: 100%;

    .theme--light.v-label {
      color: rgba(54, 60, 86, 0.35);
      font-weight: 400;
    }

    &.v-text-field > .v-input__control > .v-input__slot {
      font-weight: 500;
    }
  }

  .dialog-message {
    @include font-size(14px);
    line-height: 17px;
    text-align: center;
    color: $grey-darker;
    opacity: 0.8;
    font-weight: 400;
    word-break: normal;
    margin: $spacing * 1.25 $spacing * -5;
  }
}
</style>
